import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {FormLayout} from '@/types/form-layout'

function fetchLayoutOrNull(fetcher: () => Promise<FormLayout>): () => Promise<FormLayout | null> {
    return async () => {
        try {
            return await fetcher()
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'status' in err && (err as {status: number}).status === 404) {
                return null
            }
            throw err
        }
    }
}

/**
 * TanStack Query hook for fetching a single form layout by use-case key (render mode).
 * Hidden fields are stripped by the server. Returns 404 as null (no layout saved yet).
 */
export function useFormLayout(useCaseKey: string) {
    const services = useServices()
    return useQuery<FormLayout | null>({
        queryKey: ['form-layouts', useCaseKey],
        queryFn: fetchLayoutOrNull(() => services.formLayouts.detail(useCaseKey)),
        retry: false,
    })
}

/**
 * TanStack Query hook for fetching a form layout in designer mode (includes hidden fields).
 * Requires form-layouts:manage permission. Returns 404 as null (no layout saved yet).
 */
export function useFormLayoutFull(useCaseKey: string) {
    const services = useServices()
    return useQuery<FormLayout | null>({
        queryKey: ['form-layouts', useCaseKey, 'full'],
        queryFn: fetchLayoutOrNull(() => services.formLayouts.getFull(useCaseKey)),
        retry: false,
    })
}
